import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import LegalPage from "./LegalPage";

const FAQS = [
  {
    id: "media-links",
    q: "How do the media links in the PDF work?",
    a: (
      <p>
        Our PDF generation tool uses relative file paths to keep your bundle self-contained. 
        <strong> Note:</strong> Many built-in PDF viewers, including macOS Preview, Chrome, and Safari, disable external links for security. 
        For full functionality, including clicking images and videos to open their original, full-resolution source files, please use a dedicated PDF application such as <strong>Adobe Acrobat</strong> or <strong>Foxit</strong>. 
        As long as you keep the PDF and the <code>media/</code> folder together in the same directory, these dedicated applications will correctly open your files.
      </p>
    ),
  },
  {
    id: "verification",
    q: "Can a third party (like an Adjudicator) verify the chat?",
    a: (
      <p>
        Yes. Every document bundle includes a unique SHA-256 cryptographic hash of the
        original source file. Any third party can independently verify that the PDF
        produced matches the original file exactly, ensuring that no messages were added,
        removed, or altered during the conversion process.
      </p>
    ),
  },
  {
    id: "retention",
    q: "Is my data stored on your servers?",
    a: (
      <p>
        No. We operate a strict Zero-Retention policy. Your uploaded source files are
        processed in an ephemeral environment and are permanently destroyed within 60
        minutes of generation. We do not retain copies of your chat logs, nor do we store
        them in any long-term database.
      </p>
    ),
  },
  {
    id: "processing",
    q: "Why does my chat upload take a few minutes to process?",
    a: (
      <>
        <p>
          We don&rsquo;t just convert your file, we secure it. When you upload your WhatsApp
          export, our system performs a series of quality assurance checks to ensure your document is professional and ready for formal use::
        </p>
        <ol>
          <li>
            <strong>Document Validation:</strong> We analyse the chat structure to ensure all messages, timestamps, and media are mapped correctly, preventing the formatting errors common in other tools.
          </li>
          <li>
            <strong>Media Preparation:</strong> We optimise your images and videos to ensure they remain high-resolution and readable for formal review, while managing the file size for easy sharing.
          </li>
          <li>
            <strong>Security Finalisation:</strong> We perform a final security validation to lock the document’s integrity, ensuring you receive a clean, verifiable record.
          </li>
        </ol>
      </>
    ),
  },
];

export default function Faq() {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.hash]);

  return (
    <LegalPage
      title="FAQ | AuditPDF"
      canonical="https://auditpdfhq.com/faq"
      description="Frequently asked questions about AuditPDF: media links inside the PDF, third-party verification, zero-retention data handling, and processing times.">
      <article className="prose prose-slate max-w-4xl mx-auto py-12 px-4 md:px-0" data-testid="faq-page">
        <h1 data-testid="faq-title">FAQ</h1>
        <p>
          Answers to the most common questions about data integrity, security, and how
          your document bundle works.
        </p>
        {FAQS.map((item) => (
          <section key={item.id} id={item.id} data-testid={`faq-${item.id}`}>
            <h2>Q: {item.q}</h2>
            {item.a}
          </section>
        ))}
      </article>
    </LegalPage>
  );
}
